/**
 * **Which template draws which section in its own markup** — one list, for the
 * builder to read (FR-I14's theme rule, ADR-0020 §2, T-4.34).
 *
 * ## Why this file exists at all
 *
 * The truth is `Template.sections` in `templates/index.ts`: a map of block type
 * to a React component, and a type a template does not name is drawn by the
 * shared renderer in `blocks/index.tsx`. That map is the authority and nothing
 * here replaces it.
 *
 * But the **builder** needs the same fact, and the builder is the SPA. It
 * cannot import `templates/index.ts`: that module pulls every theme, every
 * renderer and every stylesheet string in this directory into the staff
 * bundle, to read a set of keys. So the fact travels as data — no imports, no
 * components, nothing but names.
 *
 * ## And why that is safe rather than the usual duplication
 *
 * A duplicated list that nothing compares is a list that drifts, which is what
 * `tests/site-vocabulary.test.ts` was written about after the two halves
 * disagreed on four vocabularies at once. So this one **is** compared:
 * `tests/site-editor.test.tsx` derives the same map from `TEMPLATES` and fails
 * if the two differ, in either direction.
 *
 * **If that test fails, this file is what to change, and it is one line.** A
 * template that starts drawing a section in its own markup — which is the whole
 * of Wave 8D and 8E — adds the type here too, because a firm on that template
 * is told in the builder which sections it draws itself, and a stale list tells
 * it the wrong thing about its own website.
 *
 * ## What it does not say
 *
 * Not naming a section is **not** a lesser template. Every template restyles
 * every section through its own CSS — `cma-costline` names two and looks
 * nothing like `practice-classic` — and FR-I14 is explicit that a template
 * that names none still draws all twenty-two. This list is about *markup*, and
 * the builder's wording says markup. (The example used to be `ca-ledger`,
 * which named none; Wave 8D gave it sixteen.)
 */

import type { BlockType } from './types'

export const THEME_SECTIONS: Record<string, readonly BlockType[]> = {
  // Wave 8D's four. Each draws **all thirteen** of FR-I14 in its own markup
  // as well as the nine it already had, because an inside page is built out
  // of the library and these four had nothing of their own to draw it with —
  // the defect T-4.29 closes. The nine come first on each line, then the
  // thirteen.
  // Wave 10H (T-4.52): all twenty.
  'practice-modern': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta',
  ],
  'advocate-chambers': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta',
  ],
  'practice-atelier': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta',
  ],
  'ca-ledger': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta',
  ],
  // Wave 8E's four. Each draws every one of the twenty-two in its own
  // stylesheet — which is where a theme's identity lives — and the types below
  // are the ones whose **markup** it also replaces (ADR-0020 §2): an
  // arrangement the shared renderer cannot express, or `team`'s monogram for a
  // member who gave no photograph.
  'practice-classic': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta',
  ],
  'cs-charter': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta',
  ],
  'cma-costline': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta',
  ],
  'advocate-counsel': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta',
  ],
  // C17 (ADR-0036 §1), Wave 18C (T-4.71): the twenty and the three event
  // sections — twenty-three, the only line that counts the three (§3).
  'event-encore': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'countdown', 'schedule', 'tickets',
  ],
  // C21 (ADR-0043 §1, §4), Wave 21B's four stubs: **empty**, because a stub
  // draws every section with the shared renderers. Each theme session
  // (21C–21F) fills its own line with the twenty, the eight generic sections
  // of C21 and its family's verticals — twenty-eight for these four.
  // Wave 21C (T-4.86): the twenty and C21's eight generic sections.
  'finance-meridian': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours',
  ],
  // Wave 21D (T-4.87): the twenty and C21's eight generic sections.
  'corporate-keystone': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours',
  ],
  // Wave 21E (T-4.88): the twenty and C21's eight generic sections.
  'agency-kinetic': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours',
  ],
  // Wave 21F (T-4.89): the twenty and C21's eight generic sections.
  'startup-orbit': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours',
  ],
  // C21 (ADR-0043 §1, §4), Wave 22A's four stubs: **empty**, because a stub
  // draws every section with the shared renderers. Each theme session
  // (22B–22E) fills its own line with the twenty, the eight generic sections
  // of C21 and its family's verticals — 29, 29, 30 and 29 (waves.md I-4).
  // Wave 22B (T-4.94).
  'care-haven': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours', 'price_list',
  ],
  // Wave 22C (T-4.95).
  'wellness-stillwater': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours', 'price_list',
  ],
  // Wave 22D (T-4.96).
  'learning-campus': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours', 'courses', 'openings',
  ],
  // Wave 22E (T-4.97).
  'cause-kindred': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours', 'goal',
  ],
  // C21 (ADR-0043 §1, §4), Wave 23A's five stubs: **empty**, because a stub
  // draws every section with the shared renderers. Each theme session
  // (23B–23F) fills its own line with the twenty, the eight generic sections
  // of C21 and its family's verticals — 29, 29, 29, 29 and 28 (waves.md I-5).
  // Wave 23B (T-4.100).
  'grooming-blade': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours', 'price_list',
  ],
  // Wave 23C (T-4.101).
  'food-hearth': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours', 'price_list',
  ],
  // Wave 23D (T-4.102).
  'travel-voyage': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours', 'product_grid',
  ],
  // Wave 23E (T-4.103).
  'retail-maison': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours', 'product_grid',
  ],
  // Wave 23F (T-4.104).
  'web3-cipher': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours',
  ],
  // C23 (ADR-0045 §1), Wave 25: Summit — the twenty and C21's eight generic
  // sections, like the four professional themes it sits beside.
  'practice-summit': [
    'hero', 'about', 'services', 'team', 'contact', 'faq', 'blog_list',
    'page_hero', 'showcase', 'metrics', 'split_feature',
    'product_spotlight', 'feature_grid', 'logo_strip', 'testimonial_strip',
    'statement', 'steps', 'story_strip', 'lead_form',
    'cta', 'pricing', 'tabs', 'timeline', 'video', 'before_after',
    'marquee', 'comparison', 'hours',
  ],
}

/** The sections `code` draws in its own markup — empty for a template that
 *  draws every section with the shared renderers, which is most of them. */
export function themeSections(code: string | null | undefined): readonly BlockType[] {
  return (code && THEME_SECTIONS[code]) || []
}
